import { useMemo, useState } from "react";

/* La tabla del RTO, con un filtro por columna. LA MISMA PARA FURGONES Y PARA
 * TRACTORES.
 *
 * ── POR QUÉ ES UN COMPONENTE Y NO DOS TABLAS PARECIDAS ───────────────────
 *
 * Nico lo pidió con esas palabras: «que tanto Furgones como Tractores tuvieran
 * la misma vista». Copiada, el día que se toque cómo se dice «Sin dato» o cómo
 * se ordena una columna, las dos pantallas contestarían distinto sobre la
 * misma clase de pregunta — y nadie mira las dos el mismo día, así que la
 * diferencia vive meses. Es la razón por la que `Ubicacion`, `ChipUsuario`,
 * `VeredictoRTO` y `BloqueFicha` salieron de adentro de sus pantallas.
 *
 * LOS DATOS NO SE COMPARTEN Y LA VISTA SÍ: un tractor no tiene lona, ni
 * puerta, ni tipo de acoplado. Cada pantalla pasa SUS columnas.
 *
 * ── EL FILTRO POR COLUMNA CUENTA LO QUE VA A QUEDAR ──────────────────────
 *
 * Las opciones de cada columna salen de los datos —no de una lista escrita a
 * mano, que el día que aparezca un valor nuevo no lo ofrecería— y se calculan
 * **aplicando los OTROS filtros primero**. Sin eso, la lista ofrecería
 * «Vencido · 74» estando ya filtrado por empresa, y al elegirlo aparecerían
 * tres filas: un número que no se cumple hace dudar de la pantalla entera.
 * Y una opción que dejaría cero no se ofrece.
 *
 * ── Y LO QUE EL FILTRO SACA SE DICE ──────────────────────────────────────
 *
 * Con filtros puestos se muestra «N de M». Una lista recortada sin avisar se
 * lee como la lista completa — misma regla que el «y 5 unidades más» del
 * buscador y que los conteos del padrón.
 */

export const TODOS = "";

/* El valor de una celda para FILTRAR y AGRUPAR. Se usa el mismo texto que se
 * dibuja: con uno para mostrar y otro para filtrar, elegir «Sin dato» podría
 * no traer las filas que dicen «Sin dato». */
const valorDe = (col, f) => {
  const v = col.valor ? col.valor(f) : f?.[col.clave];
  return v === null || v === undefined || v === "" ? "—" : String(v);
};

function opciones(columna, filas) {
  const cuenta = new Map();
  for (const f of filas) {
    const v = valorDe(columna, f);
    cuenta.set(v, (cuenta.get(v) || 0) + 1);
  }
  return [...cuenta.entries()].sort((a, b) =>
    a[0].localeCompare(b[0], "es", { numeric: true })
  );
}

/* `atributosFila` (opcional): (fila) → atributos extra para su `<tr>`. Lo usa
 * Multas para marcar con `data-ver` la unidad del acta que pidió el buscador
 * (Igna, 02/10: «que se abra en el ítem exacto, no solo la pantalla»). */
export default function TablaUnidades({ columnas, filas, vacio, atributosFila }) {
  const [puestos, setPuestos] = useState({});

  const activos = useMemo(
    () => Object.entries(puestos).filter(([, v]) => v !== TODOS),
    [puestos]
  );

  /* Todas menos la de una columna: es lo que hace que los números de su
   * desplegable sean los que se van a cumplir al elegir. */
  const pasaSalvo = useMemo(() => {
    return (fila, salvo) =>
      activos.every(([clave, valor]) => {
        if (clave === salvo) return true;
        const col = columnas.find((c) => c.clave === clave);
        return !col || valorDe(col, fila) === valor;
      });
  }, [activos, columnas]);

  const visibles = useMemo(
    () => (filas || []).filter((f) => pasaSalvo(f, null)),
    [filas, pasaSalvo]
  );

  const total = (filas || []).length;

  return (
    <>
      {activos.length > 0 && (
        <p className="tu-recorte">
          Mostrando <b>{visibles.length}</b> de {total}.{" "}
          <button
            type="button"
            className="tu-limpiar"
            onClick={() => setPuestos({})}
          >
            Quitar los filtros
          </button>
        </p>
      )}

      {/* `html, body` llevan `overflow-x: hidden`: sin el envoltorio, lo que no
          entra no se desplaza — se recorta, y las últimas columnas quedan
          inalcanzables en un teléfono. */}
      <div className="tabla-wrap">
        <table className="tabla fija">
          <thead>
            <tr>
              {columnas.map((c) => (
                <th key={c.clave}>{c.titulo}</th>
              ))}
            </tr>
            <tr className="tu-filtros">
              {columnas.map((c) => {
                if (!c.filtro) return <th key={c.clave} />;
                const base = (filas || []).filter((f) => pasaSalvo(f, c.clave));
                const ops = opciones(c, base);
                const puesto = puestos[c.clave] ?? TODOS;
                return (
                  <th key={c.clave}>
                    <select
                      className={"tu-select" + (puesto !== TODOS ? " activo" : "")}
                      value={puesto}
                      aria-label={`Filtrar por ${c.titulo}`}
                      onChange={(e) =>
                        setPuestos((p) => ({ ...p, [c.clave]: e.target.value }))
                      }
                    >
                      <option value={TODOS}>Todos · {base.length}</option>
                      {ops.map(([v, n]) => (
                        <option key={v} value={v}>
                          {v} · {n}
                        </option>
                      ))}
                    </select>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibles.map((f, i) => (
              <tr key={f.__clave || i} {...(atributosFila?.(f) || {})}>
                {columnas.map((c) => (
                  <td key={c.clave} className={c.clase ? c.clase(f) : undefined}>
                    {c.celda ? c.celda(f) : valorDe(c, f)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibles.length === 0 && (
        <p className="tk-vacio">
          {activos.length > 0
            ? "Ninguna unidad con esos filtros."
            : vacio || "No hay nada cargado."}
        </p>
      )}
    </>
  );
}
